import { renderStoreOpeningMarkup } from './StoreOpeningStatus.js';
import { formatDistance } from './StoreDistance.js';

/**
 * Construction de la carte de résultat complète (chantier
 * store-locator-revendeurs, point 4, critère 4, scénarios 24-37). Fichier
 * séparé du composant `StoreResultsList.js` qui l'appelle, pour rester
 * testable indépendamment (pas de dépendance au DOM en dehors de la lecture
 * du conteneur `#store-opening-texts`, déjà utilisée au point 3).
 *
 * Ordre des éléments dans la carte (critère 4) : badge de type, nom en gras,
 * adresse, services disponibles avec picto, statut d'ouverture + « Voir
 * horaires » (bloc réutilisé du point 3), téléphone, « Itinéraire », « En
 * savoir plus ». La distance (si un point de référence existe) est affichée
 * à part, en haut à droite (position CSS, hors du flux de lecture).
 *
 * Zone de suivi des clics (invariants R4/R13) : deux éléments distincts
 * portent la classe `.list-item` avec `data-title`/`data-url` — le nom et
 * « En savoir plus », seuls comptés par le suivi existant (voir annexe du
 * chantier, tableau des revues, écart accepté par Robin le 2026-09-23,
 * scénario 59b). Téléphone, Itinéraire et « Voir horaires » sont des éléments
 * distincts, jamais des descendants d'un `.list-item` (qui rendrait leurs
 * clics invisibles au visiteur : `.list-item * { pointer-events: none; }`).
 *
 * Bouton de démonstration : retiré (retours de Robin du 28/09, critère 14
 * modifié — « aucun bouton, nulle part »). L'action réelle de sélection a
 * ensuite été abandonnée par Robin (28/09) : ni lot A, ni lot B (O'XYZ).
 */

/**
 * Échappe une chaîne pour une insertion sûre en contenu ou attribut HTML.
 * N'est PAS appliqué à `address` : déjà nettoyé côté serveur par
 * `htmlspecialchars()` avant l'encodage JSON (voir archive-stores.php) — le
 * réappliquer produirait un double-encodage des entités (ex. « & » -> «
 * &amp;amp; »).
 * @param  {*} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char]);
}

/** Libellés des types de magasin (badge), alignés sur les slugs de la taxonomie `type`. */
const TYPE_BADGE_CLASS = {
  'concept-store': 'sl-result-card__badge--concept-store',
  'corner-store': 'sl-result-card__badge--corner-store',
  revendeur: 'sl-result-card__badge--revendeur',
};

/**
 * Pictos des services (champs ACF `custom_frame`, `standard_frame`,
 * `online_print` — critère 4). Pictos pleins fournis par Robin (05/10, chantier
 * store-locator-pictos-services) : `fill="currentColor"` (couleur du texte de
 * la carte), sans largeur/hauteur — la taille vient du CSS
 * (`.sl-result-card__service svg`). Mêmes dessins que archive-stores.php
 * (puces de filtre) et single-stores.php (page magasin).
 */
const SERVICE_ICONS = {
  // Viewbox conservés tels que fournis (512, 380, 32), tous carrés.
  custom_frame: {
    label: 'Cadres sur mesure',
    svg: '<svg viewBox="0 0 512 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M492.499,19.502c-26.011-26.001-68.329-26.006-94.329,0.006c-33.372,33.371-69.409,69.407-143.827,143.822 c-1.77,1.764-3.116,3.909-3.946,6.27l-38.24,108.99c-2.122,6.046-0.586,12.777,3.946,17.31c4.529,4.529,11.239,6.068,17.31,3.946 l108.99-38.24c2.361-0.83,4.506-2.177,6.27-3.946c74.374-74.374,110.474-110.474,143.827-143.827 C518.499,87.825,518.499,45.513,492.499,19.502z M255.114,256.887l17.271-49.223l31.953,31.953L255.114,256.887z M468.919,90.251 l-23.583,23.583l-47.169-47.166l23.582-23.582c13-13.005,34.164-13.005,47.17,0C481.913,56.092,481.913,77.245,468.919,90.251z"/><path d="M506.481,483.536c-22.146-22.145-96.612-96.613-180.742-180.742l-81.286,28.523c-5.368,1.873-10.927,2.817-16.56,2.817 c-6.661,0-13.12-1.298-19.095-3.765l53.118,53.118c10.486,10.474,3.037,28.464-11.789,28.464h-133.4 c-9.211,0-16.675-7.464-16.675-16.675v-133.4c0-14.855,18.018-22.26,28.464-11.789l53.398,53.398 c-4.72-11.262-5.382-24.091-1.223-35.941l28.52-81.279C124.954,102.008,50.415,27.468,28.465,5.519 C18.024-4.958,0.001,2.46,0.001,17.309V145.15h33.35c9.211,0,16.675,7.464,16.675,16.675c0,9.211-7.464,16.675-16.675,16.675 H0.001v33.35h33.35c9.211,0,16.675,7.464,16.675,16.675c0,9.211-7.464,16.675-16.675,16.675H0.001v33.35h33.35 c9.211,0,16.675,7.464,16.675,16.675c0,9.211-7.464,16.675-16.675,16.675H0.001v33.35h33.35c9.211,0,16.675,7.464,16.675,16.675 c0,9.211-7.464,16.675-16.675,16.675H0.001v33.35h33.35c9.211,0,16.675,7.464,16.675,16.675c0,9.211-7.464,16.675-16.675,16.675 H0.001v50.025c0,9.211,7.464,16.675,16.675,16.675h478.016C509.527,512,516.953,494.007,506.481,483.536z"/></svg>',
  },
  standard_frame: {
    label: 'Cadres standards',
    svg: '<svg viewBox="0 0 380 380" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M370,35.025H10c-5.522,0-10,4.477-10,10v289.949c0,5.522,4.478,10,10,10h360c5.522,0,10-4.478,10-10V45.025 C380,39.502,375.522,35.025,370,35.025z M342.285,307.26H37.715V72.74h304.57V307.26z"/><path d="M75.555,283.303h228.891c5.121,0,9.271-4.151,9.271-9.272v-53.155c0-2.42-0.943-4.743-2.637-6.476l-25.474-26.114 c-3.475-3.559-9.133-3.745-12.83-0.425l-48.028,43.106l-82.928-75.259c-3.35-3.038-8.398-3.219-11.955-0.428 c-11.679,9.165-37.859,29.708-60.033,47.094c-2.241,1.758-3.551,4.448-3.551,7.297v64.358 C66.282,279.15,70.434,283.303,75.555,283.303z"/><path d="M227.602,197.822c11.483,0,20.826-9.343,20.826-20.825c0-11.483-9.343-20.825-20.826-20.825 c-11.48,0-20.823,9.342-20.823,20.825C206.777,188.479,216.119,197.822,227.602,197.822z"/></svg>',
  },
  online_print: {
    label: 'Impression',
    svg: '<svg viewBox="0 0 32 32" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M0 22.016q0 2.496 1.76 4.224t4.256 1.76v2.016q0 0.832 0.576 1.408t1.408 0.576h16q0.832 0 1.408-0.576t0.608-1.408v-2.016q2.464 0 4.224-1.76t1.76-4.224v-6.016q0-2.464-1.76-4.224t-4.224-1.76v-8q0-0.832-0.608-1.408t-1.408-0.608h-16q-0.832 0-1.408 0.608t-0.576 1.408v8q-2.496 0-4.256 1.76t-1.76 4.224v6.016zM4 24v-1.984q0-1.664 1.184-2.848t2.816-1.152h16q1.664 0 2.816 1.152t1.184 2.848v1.984h-1.984v-1.984q0-0.832-0.608-1.408t-1.408-0.608h-16q-0.832 0-1.408 0.608t-0.576 1.408v1.984h-2.016zM8 30.016v-8h16v8h-16zM8 10.016v-8h16v8h-1.984v-6.016h-12v6.016h-2.016zM12 10.016v-4h8v4h-8zM22.016 16v-1.984h1.984v1.984h-1.984zM26.016 16v-1.984h1.984v1.984h-1.984z"/></svg>',
  },
};

/**
 * Cherche le texte d'horaires non reconnu d'un magasin (rendu par PHP dans un
 * `<template>` caché, indexé par URL — voir archive-stores.php et le point 3
 * de ce chantier). Jamais de texte libre transporté dans un attribut HTML.
 * @param  {string} url
 * @returns {string|null}
 */
export function findUnrecognizedOpeningText(url) {
  const container = document.querySelector('#store-opening-texts');
  if (!container || !url) {
    return null;
  }
  const template = container.querySelector(`template[data-store-url="${CSS.escape(url)}"]`);
  return template ? template.innerHTML : null;
}

/**
 * Construit le HTML des pictos de service disponibles pour un magasin.
 * Aucun picto rendu si aucun service n'est renseigné (scénario 33 : « sans
 * ligne vide ni décalage »).
 * @param  {object} item
 * @returns {string}
 */
function buildServicesMarkup(item) {
  const active = ['custom_frame', 'standard_frame', 'online_print'].filter((key) => Boolean(item[key]));
  if (active.length === 0) {
    return '';
  }
  // Picto ET libellé visible (maquette, revue du conducteur point 6) : un
  // picto seul (`sr-only`) ne suffisait pas à identifier le service sans
  // survol.
  const list = active
    .map((key) => {
      const { label, svg } = SERVICE_ICONS[key];
      return `<li class="sl-result-card__service" title="${escapeHtml(label)}">${svg}<span>${escapeHtml(label)}</span></li>`;
    })
    .join('');
  return `<ul class="sl-result-card__services">${list}</ul>`;
}

/**
 * Construit la carte de résultat complète d'un magasin.
 * @param  {object} item Magasin (forme du tableau `$features` de archive-stores.php), enrichi de `__distanceKm` par `sortItemsByDistance()`.
 * @param  {object} options
 * @param  {number|null} options.distanceKm Distance en kilomètres à afficher, ou `null` si aucun point de référence (critère 5, scénario 12a).
 * @param  {number} options.index Index dans la liste triée, pour relier la carte à son bloc `.store-opening` (`data-store-index`).
 * @returns {string}
 */
export function buildStoreCardMarkup(item, { distanceKm = null, index = 0 } = {}) {
  const {
    type,
    type_label: typeLabel,
    title,
    address,
    url,
    phone,
    lat,
    lng,
    opening_hours_week: week,
  } = item;

  const normalizedWeek = week && Object.keys(week).length > 0 ? week : null;
  const unrecognizedText = normalizedWeek ? null : findUnrecognizedOpeningText(url);
  const openingHtml = renderStoreOpeningMarkup(normalizedWeek, unrecognizedText, ` data-store-index="${index}"`);

  // Badge absent pour un magasin sans terme de taxonomie (revue du
  // conducteur, point 6 : une pastille vide apparaissait auparavant pour les
  // quelques magasins « sans terme » recensés au point 1 — rien n'est rendu
  // plutôt qu'un badge sans texte).
  const badgeHtml = typeLabel
    ? `<span class="sl-result-card__badge ${TYPE_BADGE_CLASS[type] || 'sl-result-card__badge--revendeur'}">${escapeHtml(typeLabel)}</span>`
    : '';

  const distanceHtml = distanceKm !== null && Number.isFinite(distanceKm)
    ? `<span class="sl-result-card__distance">${escapeHtml(formatDistance(distanceKm))}</span>`
    : '';

  // tel: sans espaces (revue d'implémentation) : un numéro saisi avec des
  // espaces ("tel:01 23 45 67 89") reste composable sur la plupart des
  // téléphones mais pas garanti partout ; le texte affiché garde le numéro
  // tel que saisi sur la fiche. Picto (rendu visuel, point 6) : purement
  // décoratif, `aria-hidden`, aucune information supplémentaire portée par lui.
  const phoneHtml = phone
    ? `<a class="sl-result-card__phone" href="tel:${escapeHtml(String(phone).replace(/\s+/g, ''))}"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.62 1.5h2.1l1.05 2.63-1.31 1.06a7.35 7.35 0 0 0 3.35 3.35l1.06-1.31L11.5 8.28v2.1a1.05 1.05 0 0 1-1.14 1.05A9.63 9.63 0 0 1 1.57 2.64 1.05 1.05 0 0 1 2.62 1.5Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>${escapeHtml(phone)}</a>`
    : '';

  const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);
  // Picto flèche à gauche du texte (retours de Robin du 28/09, point 7),
  // même tracé que single-stores.php (bouton Itinéraire de la page magasin)
  // pour rester cohérent entre les deux.
  const directionsHtml = hasCoordinates
    ? `<a class="sl-result-card__directions" href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(lat)},${encodeURIComponent(lng)}" target="_blank" rel="noopener"><svg width="14" height="14" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3.33317 17.2346L3.33317 15.2346C3.33317 12.4343 3.33317 11.0342 3.87814 9.96463C4.35751 9.02382 5.12241 8.25891 6.06322 7.77955C7.13278 7.23458 8.53291 7.23458 11.3332 7.23458L16.6665 7.23458M16.6665 7.23458L13.3332 3.90124M16.6665 7.23458L13.3332 10.5679" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>Itinéraire</a>`
    : '';

  return `
    <div class="sl-result-card" data-store-index="${index}">
      ${distanceHtml}
      ${badgeHtml}
      <a class="list-item sl-result-card__name" data-title="${escapeHtml(title)}" data-url="${escapeHtml(url)}" href="${escapeHtml(url)}">${escapeHtml(title)}</a>
      <p class="sl-result-card__address">${address || ''}</p>
      ${buildServicesMarkup(item)}
      ${openingHtml}
      <div class="sl-result-card__contact">
        ${phoneHtml}
        ${directionsHtml}
      </div>
      <a class="list-item sl-result-card__more" data-title="${escapeHtml(title)}" data-url="${escapeHtml(url)}" href="${escapeHtml(url)}">En savoir plus</a>
    </div>
  `;
}
